import { Component, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import { Organization } from "../organization/dto/Organization";
import { OrganizationWithAccesses } from "../../core/component/hornavbar/dto/InitDataDTO";
import { OrganizationAccesses, OrganizationsService } from "../organization/components/service/organizations.service";
import { EmployeeCrudDTO } from "../company/dto/EmployeeCRUDDTO";
import { AuthService } from "../auth/service/auth.service";
import { InitStateService } from "../../core/service/init-state.service";
import { EmployeeService } from "../organization/service/employee.service";
import { NotificationsService } from "../../core/service/notifications.service";
import {
  FundAndAction,
  FundModalType,
} from "../organization/components/shared/modal-fund-update/modal-fund-update.component";
import { ManagementEntity } from "../organization/dto/ManagementEntity";
import { Fund } from "../fund-performance/dto/Fund";
import { ManagementEntityService } from "../organization/service/management-entity.service";
import { UserManagementComponent } from "../user-management/user-management.component";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { OrgUserManagementUrls } from "./module/org-user-mngmnt-routing";
import { FundsEntitiesPageService } from "../organization/components/service/funds-entites-page.service";

@Component({
  selector: 'app-org-user-management',
  templateUrl: './org-user-management.component.html',
  styleUrl: './org-user-management.component.scss',
})
export class OrgUserManagementComponent implements OnInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  protected readonly FundModalType = FundModalType;
  protected readonly TabsOrgAndUserManagement = TabsOrgAndUserManagement;

  @ViewChild('userManagement') userManagement: UserManagementComponent;
  isAdmin = false;
  show = new ShowModal();
  showUserManagementOptions = false;
  tabNames: TabsOrgAndUserManagement[] = Object.values(TabsOrgAndUserManagement);
  selectedTab: TabsOrgAndUserManagement = TabsOrgAndUserManagement.ENTITIES;
  tabIndexMap: { [key in TabsOrgAndUserManagement]: number } = {
    [TabsOrgAndUserManagement.ENTITIES]: 0,
    [TabsOrgAndUserManagement.USERS]: 1,
  };

  selectedOrgWithAccesses: OrganizationWithAccesses;
  orgAccesses: OrganizationAccesses;
  managementCompanies: ManagementEntity[] = [];
  otherEntities: ManagementEntity[] = [];

  employees: EmployeeCrudDTO[];
  userForModal: EmployeeCrudDTO;
  deleteMemberTextList: string[];

  showOrgUpdateModal = false;
  showFundUpdateModal = false;
  showModalLegalFundInfoCrud = false;
  managementEntityForUpdate: ManagementEntity;
  fundForModal: Fund;
  createManagementCompany = false;
  fundModalType: 'create' | 'update' | 'copy';

  constructor(
    public organizationsService: OrganizationsService,
    private authService: AuthService,
    private initService: InitStateService,
    private employeeService: EmployeeService,
    private notificationService: NotificationsService,
    private managementEntityService: ManagementEntityService,
    private fundEntitiesPageService: FundsEntitiesPageService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.router.events.pipe(takeUntil(this._destroyStream)).subscribe((res) => {
      if (!(res instanceof NavigationEnd)) {
        return;
      }
      this.processRouterEvent(res);
    });
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  changeTab(tab: TabsOrgAndUserManagement): void {
    this.selectedTab = tab;
    if (this.selectedTab === TabsOrgAndUserManagement.ENTITIES) {
      this.router.navigate([OrgUserManagementUrls.base, OrgUserManagementUrls._orgSettings], { queryParams: this.route.snapshot.queryParams });
    } else if (this.selectedTab === TabsOrgAndUserManagement.USERS) {
      this.router.navigate([OrgUserManagementUrls.base, OrgUserManagementUrls._userSettings], { queryParams: this.route.snapshot.queryParams });
    }
  }

  openCreateEntityModal(isManagementEntity: boolean): void {
    this.fundEntitiesPageService.setIsCreateManagementCompany(isManagementEntity);
    this.fundEntitiesPageService.setShowModalLegalFundInfo(true);
  }

  showAddTeamModal(): void {
    this.userForModal = null;
    this.userManagement.showAddTeamModal();
  }

  async closeModalDeleteUser(isConfirmed: boolean): Promise<void> {
    this.show.deleteEmployee = false;
    if (isConfirmed) {
      await this.employeeService.delete(this.userForModal.uuid);
      this.employees = this.employees.filter((e) => e.uuid !== this.userForModal.uuid);
    }
  }

  updatedOrg(org: Organization): void {
    this.show.orgUpdate = false;
    if (org) {
      this.initService.refreshInitData();
    }
  }

  showUpdateFundModal(fundAction: FundAndAction): void {
    this.fundEntitiesPageService.setFundModalAction(fundAction);
    this.fundEntitiesPageService.setShowFundUpdateModal(true);
  }

  async getManagementEntities(): Promise<void> {
    const res = await this.managementEntityService.get(this.selectedOrgWithAccesses.organization.uuid);
    this.managementCompanies = res.filter((comp) => comp.type === 'Management company');
    this.otherEntities = res.filter((other) => other.type === 'Other entity');
    this.selectedOrgWithAccesses.organization.funds.forEach((fund) => {
      fund.managementCompany = null;
      this.managementCompanies.forEach((el) => {
        if (el.fundIds?.includes(fund.id)) {
          fund.managementCompany = el;
        }
      });
    });
    this.selectedOrgWithAccesses.organization.managementEntities = this.managementCompanies;
  }

  refreshEntities(): void {
    this.getManagementEntities();
    this.managementEntityForUpdate = null;
    this.showModalLegalFundInfoCrud = false;
  }

  private initSubscriptions(): void {
    this.organizationsService.selectedWithAccesses$
      .pipe(takeUntil(this._destroyStream))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (org): Promise<void> => {
        if (!org) {
          return;
        }
        // Do not show services, till the accesses for the selected org are fetched.
        // We know that selected org exists, therefore we do not show the list of all orgs.
        this.selectedOrgWithAccesses = org;
        this.orgAccesses = this.organizationsService.selectedOrgAccesses;
        this.showUserManagementOptions = (this.orgAccesses.isSuperAdmin || this.orgAccesses.isOrganizationAdmin) ||
          this.selectedOrgWithAccesses.userAccesses.hasAtLeastOneAccess(this.selectedOrgWithAccesses);
        if (this.showUserManagementOptions) {
          await this.getAllEmployees();
        }
        this.refreshEntities();
        this.isAdmin = (this.orgAccesses.isSuperAdmin || this.orgAccesses.isOrganizationAdmin) ||
          this.selectedOrgWithAccesses.userAccesses.isAdminSomewhere();
      });
  }

  private async getAllEmployees(): Promise<void> {
    this.employees = await this.employeeService.get(this.selectedOrgWithAccesses.organization.uuid);
    this.checkOrgAccess();
  }

  private checkOrgAccess(): void {
    const userInfo = this.authService.userInfoValue;
    this.orgAccesses.isSuperAdmin = userInfo.userRole === 'ADMINSUPER';
    this.orgAccesses.isOrganizationAdmin = this.employees.find(
      (e) => e.mail === userInfo.mail)?.organizationAdmin;
    this.orgAccesses = { ...this.orgAccesses };
  }

  private processRouterEvent(res: NavigationEnd): void {
    if (res.url.includes(OrgUserManagementUrls._orgSettings)) {
      this.changeTab(TabsOrgAndUserManagement.ENTITIES);
    } else if (res.url.includes(OrgUserManagementUrls._userSettings)) {
      this.changeTab(TabsOrgAndUserManagement.USERS);
    }
  }
}

class ShowModal {
  userManagement = false;
  orgUpdate = false;
  teamCrud = false;
  deleteEmployee = false;
}

export enum TabsOrgAndUserManagement {
  ENTITIES = 'Funds & entities',
  USERS = 'User management',
}
